import React from "react";
import { FaCapsules } from "react-icons/fa";
import useSystemVariable from "./shared/useSystemVariable";
import SystemVariablePage from "./shared/SystemVariablePage";
import { normalizeHeader, pickCell, parseNumber, safeNumber, downloadExcelTemplate } from "./shared/excelUtils";
import {
  useListMedicineTemplatesMutation, useCreateMedicineTemplateMutation,
  useUpdateMedicineTemplateMutation, useDeleteMedicineTemplateMutation,
  useBulkUpsertMedicineTemplatesMutation,
} from "../../store/systemVariablesApi";

const DEFAULT_FORM = {
  name: "", dose1: "", dose2: "", periodValue: "", periodUnit: "Gün",
  box: "", quantity: "", usageMethod: "",
};

const itemToForm = (it) => ({
  name: it?.name || "",
  dose1: safeNumber(it?.dose1),
  dose2: safeNumber(it?.dose2),
  periodValue: safeNumber(it?.usagePeriod?.value),
  periodUnit: it?.usagePeriod?.unit || "Gün",
  box: safeNumber(it?.box),
  quantity: safeNumber(it?.quantity),
  usageMethod: it?.usageMethod || "",
});

const formToPayload = (f) => ({
  name: f.name,
  dose1: parseNumber(f.dose1),
  dose2: parseNumber(f.dose2),
  usagePeriod: f.periodValue !== "" ? { value: parseNumber(f.periodValue), unit: f.periodUnit } : undefined,
  box: parseNumber(f.box),
  quantity: parseNumber(f.quantity),
  usageMethod: f.usageMethod || undefined,
});

const mapExcelRow = (row) => {
  const n = {};
  Object.keys(row || {}).forEach((k) => { n[normalizeHeader(k)] = row[k]; });
  const periodValue = pickCell(n, ["kullanım periyodu değer", "kullanim periyodu deger", "periyot", "period value", "period"]);
  const periodUnit = pickCell(n, ["kullanım periyodu birim", "kullanim periyodu birim", "periyot birim", "period unit", "unit"]);
  return {
    name: (pickCell(n, ["ilaç adı", "ilac adi", "ilaç", "ilac", "name"]) || "").toString().trim(),
    dose1: parseNumber(pickCell(n, ["doz 1", "doz1", "dose1"])),
    dose2: parseNumber(pickCell(n, ["doz 2", "doz2", "dose2"])),
    usagePeriod: periodValue !== undefined || periodUnit !== undefined
      ? { value: parseNumber(periodValue), unit: periodUnit ? String(periodUnit).trim() : undefined } : undefined,
    box: parseNumber(pickCell(n, ["kutu", "box"])),
    quantity: parseNumber(pickCell(n, ["adet", "quantity"])),
    usageMethod: (pickCell(n, ["kullanım şekli", "kullanim sekli", "usage method", "method"]) || "").toString().trim(),
  };
};

const TEMPLATE_ROWS = [
  { "İlaç Adı": "Metformin 1000mg", "Doz 1": 1, "Doz 2": 1, "Kullanım Periyodu Değer": 30, "Kullanım Periyodu Birim": "Gün", Kutu: 1, Adet: 30, "Kullanım Şekli": "Oral" },
  { "İlaç Adı": "Gliflozin 10mg", "Doz 1": 1, "Doz 2": 0, "Kullanım Periyodu Değer": 30, "Kullanım Periyodu Birim": "Gün", Kutu: 1, Adet: 30, "Kullanım Şekli": "Oral" },
  { "İlaç Adı": "İnsülin Glarjin", "Doz 1": 20, "Doz 2": 0, "Kullanım Periyodu Değer": 30, "Kullanım Periyodu Birim": "Gün", Kutu: 1, Adet: 1, "Kullanım Şekli": "Subkutan" },
];

const TABLE_COLUMNS = [
  { key: "name", label: "İlaç Adı", bold: true },
  { key: "dose1", label: "Doz 1", width: 90 },
  { key: "dose2", label: "Doz 2", width: 90 },
  { key: "usagePeriod", label: "Periyot", width: 140, render: (it) => it.usagePeriod ? `${it.usagePeriod.value ?? "-"} ${it.usagePeriod.unit || ""}` : "-" },
  { key: "box", label: "Kutu", width: 80 },
  { key: "quantity", label: "Adet", width: 80 },
  { key: "usageMethod", label: "Kullanım Şekli", width: 140 },
];

const FORM_FIELDS = [
  { key: "name", label: "İlaç Adı", required: true },
  { key: "dose1", label: "Doz 1" },
  { key: "dose2", label: "Doz 2" },
  { key: "periodValue", label: "Periyot Değer" },
  { key: "periodUnit", label: "Periyot Birim" },
  { key: "box", label: "Kutu" },
  { key: "quantity", label: "Adet" },
  { key: "usageMethod", label: "Kullanım Şekli" },
];

const FORM_SECTIONS = [
  { title: "Temel Bilgiler", fields: [{ key: "name", label: "İlaç Adı", required: true }] },
  { title: "Dozaj", fields: [
    { key: "dose1", label: "Doz 1", flex: "1 1 140px" },
    { key: "dose2", label: "Doz 2", flex: "1 1 140px" },
  ]},
  { title: "Kullanım", fields: [
    { key: "periodValue", label: "Periyot Değer", flex: "1 1 140px" },
    { key: "periodUnit", label: "Periyot Birim", flex: "1 1 140px" },
    { key: "box", label: "Kutu", flex: "1 1 100px" },
    { key: "quantity", label: "Adet", flex: "1 1 100px" },
    { key: "usageMethod", label: "Kullanım Şekli" },
  ]},
];

const IMPORT_COLUMNS = [
  { key: "name", label: "İlaç Adı", bold: true },
  { key: "dose1", label: "Doz 1" },
  { key: "dose2", label: "Doz 2" },
  { key: "usagePeriod", label: "Periyot", render: (r) => r.usagePeriod ? `${r.usagePeriod.value ?? ""} ${r.usagePeriod.unit || ""}` : "-" },
  { key: "box", label: "Kutu" },
  { key: "quantity", label: "Adet" },
  { key: "usageMethod", label: "Kullanım" },
];

export default function Medicines() {
  const vm = useSystemVariable({
    listMutation: useListMedicineTemplatesMutation(),
    createMutation: useCreateMedicineTemplateMutation(),
    updateMutation: useUpdateMedicineTemplateMutation(),
    deleteMutation: useDeleteMedicineTemplateMutation(),
    bulkMutation: useBulkUpsertMedicineTemplatesMutation(),
    itemsKey: "items",
    idKey: "medicineTemplateId",
    defaultForm: DEFAULT_FORM,
    itemToForm,
    formToPayload,
    entityLabel: "İlaç",
  });

  return (
    <SystemVariablePage
      icon={<FaCapsules size={22} />}
      title="İlaç Şablonları"
      subtitle={<>İlaç reçetesi oluştururken seçilecek ilaç listesi. Zorunlu alan: <b>İlaç Adı</b></>}
      entityLabel="İlaç"
      vm={vm}
      tableColumns={TABLE_COLUMNS}
      formFields={FORM_FIELDS}
      formSections={FORM_SECTIONS}
      importColumns={IMPORT_COLUMNS}
      mapExcelRow={mapExcelRow}
      downloadTemplate={() => downloadExcelTemplate(TEMPLATE_ROWS, "İlaçlar", "ilac_sablonu.xlsx")}
    />
  );
}
